// Copyright (c) 2026, ehabsid and contributors
// The salon website: one booking engine under every design.
//
// The page's look comes from the Salon Website settings, rendered server-side
// into section designs. This script fills the live parts — branches, services,
// stylists, times — from salon.api.booking, and drives the one booking form,
// wherever the design put it (a page section, a side panel, a popup, a
// step-by-step wizard). Booking rules never live here: the server decides.
//
// One booking is one stylist doing several services back to back, so the form
// collects a basket and a single stylist, and times are sized to the sum.

(function () {
	"use strict";

	function init() {
		var app = document.getElementById("salon-booking-app");
		if (!app) return;
		app.classList.add("sx-js");

		var $ = function (id) { return document.getElementById(id); };
		var els = {
			overlay: $("sx-overlay"),
			steps: $("sx-steps"),
			dateStrip: $("sx-date-strip"),
			toast: $("sx-toast"),
			branch: $("sb-branch"),
			services: $("sb-services"),
			basket: $("sb-basket"),
			staff: $("sb-staff"),
			date: $("sb-date"),
			slots: $("sb-slots"),
			customer: $("sb-customer"),
			name: $("sb-name"),
			phone: $("sb-phone"),
			email: $("sb-email"),
			notes: $("sb-notes"),
			confirm: $("sb-confirm"),
			selected: $("sb-selected"),
			result: $("sb-result"),
			manageBtn: $("sb-manage-btn"),
			managePhone: $("sb-manage-phone"),
			manageNumber: $("sb-manage-number"),
			manageList: $("sb-manage-list")
		};
		var form = app.querySelector(".sx-bform");
		var wizard = form && form.classList.contains("sx-wizard");
		var hasForm = !!els.branch; // absent when online booking is paused

		var state = {
			branches: [],
			chosen: [],
			slot: null,
			currency: app.getAttribute("data-currency") || "",
			catalogue: [],        // what the chosen stylist can do here
			branchCatalogue: [],  // what the branch offers at all
			step: 1
		};

		// --- the stylist box ---------------------------------------------------
		// It starts on "Choose a stylist…" and the customer picks someone or
		// First available themselves — nothing is chosen for them (Ehab,
		// 2026-09-30). First available is sent to the server as no stylist,
		// exactly as before: it picks among those free at confirmation.
		var ANY_STAFF = "__any__";
		var STAFF_PROMPT = "<option value=''>Choose a stylist…</option>";
		function staffChosen() { return !!(els.staff && els.staff.value); }
		// A branch may switch First available off (Salon Branch → Offer First
		// Available, 2026-10-15): the option is not offered and the hints stop
		// mentioning it. get_branches says so (first_available 1/0); the
		// server refuses "no stylist" there too.
		function anyAllowed() {
			var b = branchByName(els.branch.value);
			return !b || b.first_available !== 0;
		}
		function staffArg() {
			return els.staff && els.staff.value && els.staff.value !== ANY_STAFF ? els.staff.value : null;
		}

		// --- talking to the server ------------------------------------------
		// The page's own request, not frappe.call: frappe.call shows every
		// server message in the desk's white dialog (frappe/public/js/frappe/
		// request.js, v15), which reached customers' phones (Ehab, 2026-10-03).
		// It sends what frappe.call sends — a POST to /api/method/<method>,
		// form-encoded, lists as JSON, null as "", the same headers — so the
		// server sees no difference. A refusal becomes an Error carrying the
		// server's own words; each caller shows it where it belongs.
		var MSG_GENERIC = "Something went wrong on our side. Please try again in a moment.";
		var MSG_OFFLINE = "We couldn't reach the salon. Check your internet connection and try again.";
		var MSG_BUSY = "Too many tries. Please wait a minute and try again.";
		var MSG_HUMAN = "The security check is taking a moment. Please try again.";
		function problem(message, title, status) {
			var e = new Error(message || MSG_GENERIC);
			e.sxTitle = title || "";
			e.sxStatus = status || 0;
			e.sxProblem = true;
			return e;
		}
		// Frappe may send HTML in a message; the visitor only ever gets its
		// text (DOMParser runs no scripts and loads nothing).
		function plainText(html) {
			var doc = new DOMParser().parseFromString(String(html == null ? "" : html), "text/html");
			return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
		}
		// The message the server raised: Frappe sends the message log as
		// _server_messages, a JSON list of JSON objects; the raised one is
		// marked raise_exception. "Message" is Frappe's default title.
		function serverWords(data) {
			var said = { message: "", title: "" };
			var list = [];
			try {
				list = JSON.parse(data && data._server_messages || "[]").map(function (m) {
					return typeof m === "string" ? JSON.parse(m) : m;
				});
			} catch (e) { list = []; }
			var raised = list.filter(function (m) { return m && m.raise_exception; });
			var pick = (raised.length ? raised : list).slice(-1)[0];
			if (pick) {
				said.message = plainText(pick.message);
				said.title = pick.title && pick.title !== "Message" ? plainText(pick.title) : "";
			}
			return said;
		}
		function serverProblem(status, data) {
			// A server error's log can hold desk notes or internals: the
			// visitor gets the plain sentence, never those.
			if (status >= 500) return problem(MSG_GENERIC, "", status);
			var said = serverWords(data);
			if (said.message) return problem(said.message, said.title, status);
			return problem(status === 429 ? MSG_BUSY : MSG_GENERIC, "", status);
		}
		function encodeArgs(args) {
			return Object.keys(args || {}).map(function (k) {
				var v = args[k];
				if (v !== null && typeof v === "object") v = JSON.stringify(v);
				return encodeURIComponent(k) + "=" + encodeURIComponent(v == null ? "" : v);
			}).join("&");
		}
		function call(method, args) {
			var headers = {
				"Accept": "application/json",
				"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
				"X-Requested-With": "XMLHttpRequest"
			};
			if (window.frappe && window.frappe.csrf_token) headers["X-Frappe-CSRF-Token"] = window.frappe.csrf_token;
			return fetch("/api/method/salon.api.booking." + method, {
				method: "POST", headers: headers, body: encodeArgs(args), credentials: "same-origin"
			})
				.catch(function () { throw problem(MSG_OFFLINE); })
				.then(function (res) {
					return res.text().then(function (text) {
						var data = null;
						try { data = JSON.parse(text); } catch (e) { data = null; }
						if (res.ok && data) return data.message;
						throw serverProblem(res.ok ? 500 : res.status, data);
					});
				});
		}
		// Anything that is not one of ours (a script error) is shown as the
		// plain sentence and logged for whoever looks at the console.
		function asProblem(err) {
			if (err && err.sxProblem) return err;
			if (window.console) console.error(err);
			return problem(MSG_GENERIC);
		}

		// --- helpers ---------------------------------------------------------
		function el(tag, cls, text) {
			var n = document.createElement(tag);
			if (cls) n.className = cls;
			if (text != null) n.textContent = text;
			return n;
		}
		function placeholder() {
			var ph = el("div", "sx-img sx-ph");
			ph.setAttribute("aria-hidden", "true");
			return ph;
		}
		function img(url, alt) {
			if (!url) return placeholder();
			var i = el("img", "sx-img");
			i.alt = alt || "";
			i.onerror = function () { if (i.parentNode) i.parentNode.replaceChild(placeholder(), i); };
			i.src = url;
			i.loading = "lazy";
			return i;
		}
		function money(v) {
			return state.currency ? state.currency + " " + Number(v).toFixed(2) : Number(v).toFixed(2);
		}
		// "YYYY-MM-DD" as a LOCAL date — new Date(string) reads UTC midnight,
		// a day early for everyone west of Greenwich.
		function parseDay(s) {
			var p = s.split("-");
			return new Date(+p[0], +p[1] - 1, +p[2]);
		}
		function dayString(d) {
			return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") +
				"-" + String(d.getDate()).padStart(2, "0");
		}
		function toast(msg) {
			if (!els.toast) return;
			els.toast.textContent = msg;
			els.toast.classList.add("show");
			setTimeout(function () { els.toast.classList.remove("show"); }, 2600);
		}
		function each(list, fn) { Array.prototype.forEach.call(list, fn); }

		// --- messages to the visitor -----------------------------------------
		// One dialog in the site's own look (colours, fonts, corners, buttons
		// all come from the style's tokens), built once on first use. ask()
		// resolves true for the main button, false for the other one, the ×,
		// a tap outside or Esc. Only text is ever put in it.
		var dialog = null;
		function buildDialog() {
			var d = el("div", "sx-msg");
			d.hidden = true;
			var scrim = el("div", "sx-msg-scrim");
			scrim.setAttribute("data-sx-msg", "no");
			var box = el("div", "sx-msg-box");
			box.setAttribute("role", "alertdialog");
			box.setAttribute("aria-modal", "true");
			box.setAttribute("aria-labelledby", "sx-msg-title");
			box.setAttribute("aria-describedby", "sx-msg-text");
			var x = el("button", "sx-msg-close", "×");
			x.type = "button";
			x.setAttribute("aria-label", "Close");
			x.setAttribute("data-sx-msg", "no");
			var title = el("h3", "sx-msg-title");
			title.id = "sx-msg-title";
			var text = el("p", "sx-msg-text");
			text.id = "sx-msg-text";
			var extra = el("div", "sx-msg-extra");
			var actions = el("div", "sx-msg-actions");
			box.appendChild(x);
			box.appendChild(title);
			box.appendChild(text);
			box.appendChild(extra);
			box.appendChild(actions);
			d.appendChild(scrim);
			d.appendChild(box);
			app.appendChild(d);
			dialog = { root: d, box: box, title: title, text: text, extra: extra, actions: actions, done: null, back: null };
			d.addEventListener("click", function (e) {
				var b = e.target.closest("[data-sx-msg]");
				if (b) closeDialog(b.getAttribute("data-sx-msg") === "yes");
			});
			// Keep Tab inside the dialog while it is open.
			d.addEventListener("keydown", function (e) {
				if (e.key !== "Tab") return;
				var f = box.querySelectorAll("button");
				var first = f[0], last = f[f.length - 1];
				if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
				else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
			});
			return dialog;
		}
		function dialogOpen() { return !!(dialog && !dialog.root.hidden); }
		function closeDialog(answer) {
			if (!dialogOpen()) return;
			dialog.root.hidden = true;
			var done = dialog.done, back = dialog.back;
			dialog.done = dialog.back = null;
			if (back && document.contains(back) && back.offsetParent !== null) back.focus({ preventScroll: true });
			if (done) done(!!answer);
		}
		function ask(opts) {
			var d = dialog || buildDialog();
			if (dialogOpen()) closeDialog(false);   // a newer message replaces an older one
			d.title.textContent = opts.title || "Sorry";
			d.text.textContent = opts.message || "";
			d.text.hidden = !opts.message;
			// Extra lines under the message: [class, text] pairs, text only.
			d.extra.innerHTML = "";
			(opts.lines || []).forEach(function (line) {
				if (line && line[1]) d.extra.appendChild(el("div", line[0], line[1]));
			});
			d.box.classList.toggle("sx-msg-ok", !!opts.success);
			d.actions.innerHTML = "";
			if (opts.cancel) {
				var no = el("button", "sx-btn sx-btn-ghost sx-btn-sm", opts.cancel);
				no.type = "button";
				no.setAttribute("data-sx-msg", "no");
				d.actions.appendChild(no);
			}
			var yes = el("button", "sx-btn sx-btn-sm", opts.ok || "OK");
			yes.type = "button";
			yes.setAttribute("data-sx-msg", "yes");
			d.actions.appendChild(yes);
			d.back = document.activeElement;
			d.root.hidden = false;
			yes.focus({ preventScroll: true });
			return new Promise(function (resolve) { d.done = resolve; });
		}
		function showProblem(err, title) {
			err = asProblem(err);
			return ask({ title: err.sxTitle || title, message: err.message });
		}

		// A field left empty says so under itself — no dialog. The message
		// goes when the visitor types.
		function fieldError(input, text, holder) {
			if (!input) return;
			holder = holder || input.closest(".sx-field") || input.parentNode;
			var id = input.id + "-error";
			var old = document.getElementById(id);
			if (old) old.remove();
			if (!text) {
				input.removeAttribute("aria-invalid");
				input.removeAttribute("aria-describedby");
				return;
			}
			var m = el("div", "sx-field-error", text);
			m.id = id;
			m.setAttribute("role", "alert");
			holder.appendChild(m);
			input.setAttribute("aria-invalid", "true");
			input.setAttribute("aria-describedby", id);
		}
		function clearOnType(input, holder) {
			if (input) input.addEventListener("input", function () {
				if (input.value.trim()) fieldError(input, "", holder);
			});
		}
		function showField(input) {
			input.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
			input.focus({ preventScroll: true });
		}
		function groupBy(list, key) {
			var order = [], groups = {};
			list.forEach(function (x) {
				var k = x[key] || "Services";
				if (!groups[k]) { groups[k] = []; order.push(k); }
				groups[k].push(x);
			});
			return order.map(function (k) { return { name: k, items: groups[k] }; });
		}

		// --- moving around the page -----------------------------------------
		// In-page links (menu, footer, "#services" buttons) scroll smoothly and
		// leave the address alone: an address ending in #my_bookings made every
		// refresh jump back down to that section (Ehab, 2026-09-28). A refresh
		// starts at the top.
		var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if ("scrollRestoration" in history) history.scrollRestoration = "manual";
		if (location.hash && /^#[A-Za-z][\w-]*$/.test(location.hash) && document.getElementById(location.hash.slice(1))) {
			history.replaceState(null, "", location.pathname + location.search);
			window.scrollTo(0, 0);
		}
		function scrollToSection(target) {
			if (!target) return;
			if (target.id === "top") { window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" }); return; }
			var header = app.querySelector(".sx-header");
			var offset = header && getComputedStyle(header).position === "sticky" ? header.offsetHeight + 8 : 8;
			var y = target.getBoundingClientRect().top + window.pageYOffset - offset;
			window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? "auto" : "smooth" });
		}

		// --- opening the booking form ---------------------------------------
		// Inline: scroll to it. Side Panel / Popup: open the overlay.
		function openBooking() {
			if (els.overlay) {
				els.overlay.hidden = false;
				document.body.style.overflow = "hidden";
			} else {
				scrollToSection($("booking"));
			}
		}
		function closeBooking() {
			if (!els.overlay) return;
			els.overlay.hidden = true;
			document.body.style.overflow = "";
		}
		// One click handler for everything the server or this script draws:
		// the markup carries what to do in data-* attributes.
		app.addEventListener("click", function (e) {
			var t = e.target;
			var toggle = t.closest("[data-menu-toggle]");
			if (toggle) { setMenu(toggle.getAttribute("aria-expanded") !== "true"); return; }
			if (t.closest("#sx-mobile-menu a")) setMenu(false);
			var book = t.closest("[data-book]");
			if (book) { e.preventDefault(); openBooking(); return; }
			if (t.closest("[data-close]")) { closeBooking(); return; }
			var pick = t.closest("[data-branch-pick]");
			if (pick) { setBranch(pick.getAttribute("data-branch-pick")); openBooking(); return; }
			var inPage = t.closest('a[href^="#"]');
			if (inPage) {
				var dest = document.getElementById(inPage.getAttribute("href").slice(1));
				if (dest) { e.preventDefault(); scrollToSection(dest); }
				return;
			}
			var dot = t.closest("[data-slide]");
			if (dot) { slideTo(dot.closest("[data-sx-slides]"), +dot.getAttribute("data-slide"), true); return; }
			var tab = t.closest("[data-sx-tab]");
			if (tab) { showTab(tab); return; }
			var person = t.closest("[data-staff]");
			if (person) { bookWith(person.getAttribute("data-staff")); return; }
			var svc = t.closest("[data-service]");
			if (svc) bookService(svc.getAttribute("data-service"));
		});
		// A picture that cannot load (removed, or private) shows the design's
		// placeholder instead of a broken-image icon — server-drawn ones too.
		function toPlaceholder(i) {
			if (i.parentNode) i.parentNode.replaceChild(placeholder(), i);
		}
		app.addEventListener("error", function (e) {
			if (e.target.tagName === "IMG" && e.target.classList.contains("sx-img")) toPlaceholder(e.target);
		}, true);
		each(app.querySelectorAll("img.sx-img"), function (i) {
			if (i.complete && !i.naturalWidth && i.getAttribute("src")) toPlaceholder(i);
		});
		document.addEventListener("keydown", function (e) {
			if (e.key !== "Escape") return;
			// A message on top closes first; the booking form under it stays.
			if (dialogOpen()) { closeDialog(false); return; }
			if (menuOpen()) { setMenu(false); app.querySelector("[data-menu-toggle]").focus(); return; }
			closeBooking();
		});
		// A tap anywhere outside the open menu closes it.
		document.addEventListener("click", function (e) {
			if (menuOpen() && !e.target.closest(".sx-header")) setMenu(false);
		});

		// --- the phone / tablet menu -----------------------------------------
		function menuOpen() {
			var m = $("sx-mobile-menu");
			return !!m && !m.hidden;
		}
		function setMenu(open) {
			var m = $("sx-mobile-menu"), btn = app.querySelector("[data-menu-toggle]");
			if (!m || !btn) return;
			m.hidden = !open;
			btn.setAttribute("aria-expanded", open ? "true" : "false");
			btn.setAttribute("aria-label", open ? "Close menu" : "Menu");
		}

		// --- motion: reveal on scroll ---------------------------------------
		var motion = app.getAttribute("data-motion");
		if (motion && motion !== "none" && "IntersectionObserver" in window) {
			var io = new IntersectionObserver(function (entries) {
				entries.forEach(function (en) {
					if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); }
				});
			}, { threshold: 0.12 });
			each(app.querySelectorAll(".sx-reveal"), function (n) { io.observe(n); });
		} else {
			each(app.querySelectorAll(".sx-reveal"), function (n) { n.classList.add("is-in"); });
		}

		// --- hero photo spots with several photos: a slider -------------------
		// Seconds Per Photo on the Hero row (data-sx-interval): blank follows
		// the style (5 s unless its Motion is None), 0 means only by hand.
		// A visitor who asked their device for less motion gets no automatic
		// changes; dots and swipes always work. Pauses while pointed at,
		// focused, scrolled out of view, or paused with the hero's button.
		function slideTo(box, i, byHand) {
			var slides = box.querySelectorAll(".sx-slide"), dots = box.querySelectorAll(".sx-dot");
			var n = slides.length, from = box._sxAt || 0;
			var back = i < from;   // decided before wrapping: last -> first is forwards
			i = ((i % n) + n) % n;
			if (i === from) return;
			// Slide / Wipe come in from the side they are travelling from.
			box.classList.toggle("sx-back", back);
			void box.offsetWidth;
			each(slides, function (sl, k) {
				sl.classList.toggle("was-on", k === from);
				sl.classList.toggle("is-on", k === i);
				if (k === i) sl.removeAttribute("aria-hidden"); else sl.setAttribute("aria-hidden", "true");
			});
			each(dots, function (d, k) {
				d.classList.toggle("is-on", k === i);
				if (k === i) d.setAttribute("aria-current", "true"); else d.removeAttribute("aria-current");
			});
			box._sxAt = i;
			if (byHand) box._sxHeld = Date.now();   // someone chose a photo: give them time to look
		}
		function heroPaused(node) {
			var hero = node.closest("[data-sx-hero]");
			return !!hero && hero.classList.contains("is-paused");
		}
		function slideEvery(box) {
			var own = box.getAttribute("data-sx-interval");
			if (reduceMotion) return 0;
			if (own === null || own === "") return motion === "none" ? 0 : 5000;
			return Math.max(0, +own) * 1000;
		}
		each(app.querySelectorAll("[data-sx-slides]"), function (box) {
			box._sxAt = 0;
			var paused = false, visible = true;
			box.addEventListener("mouseenter", function () { paused = true; });
			box.addEventListener("mouseleave", function () { paused = false; });
			box.addEventListener("focusin", function () { paused = true; });
			box.addEventListener("focusout", function () { paused = false; });
			if ("IntersectionObserver" in window) {
				new IntersectionObserver(function (en) { visible = en[0].isIntersecting; }).observe(box);
			}
			var startX = null;
			box.addEventListener("pointerdown", function (e) { startX = e.clientX; });
			box.addEventListener("pointercancel", function () { startX = null; });
			box.addEventListener("pointerup", function (e) {
				if (startX === null) return;
				var dx = e.clientX - startX;
				startX = null;
				if (Math.abs(dx) > 40) slideTo(box, box._sxAt + (dx < 0 ? 1 : -1), true);
			});
			var every = slideEvery(box);
			box._sxAuto = every > 0;
			if (!every) return;
			setInterval(function () {
				if (paused || !visible || document.hidden || heroPaused(box)) return;
				if (box._sxHeld && Date.now() - box._sxHeld < Math.max(8000, every + 3000)) return;
				slideTo(box, box._sxAt + 1, false);   // after the last: the first, still forwards
			}, every);
		});

		// --- the rotating word (heading *word* + List Items on a Hero) ----------
		each(app.querySelectorAll("[data-sx-rotate]"), function (h) {
			var em = h.querySelector("em"), words;
			try { words = JSON.parse(h.getAttribute("data-sx-rotate")); } catch (e) { return; }
			if (!em || !words || words.length < 2 || reduceMotion) return;
			var i = 0;
			// After the heading's own entrance animation has played.
			setTimeout(function () {
				em.classList.add("sx-rot");
				setInterval(function () {
					if (document.hidden || heroPaused(h)) return;
					em.classList.add("is-out");
					setTimeout(function () {
						i = (i + 1) % words.length;
						em.textContent = words[i];
						em.classList.remove("is-out");
					}, 350);
				}, 2600);
			}, 1800);
		});

		// --- Parallax (Photo Animation on a Hero) -----------------------------
		var parallax = reduceMotion ? [] : Array.prototype.slice.call(app.querySelectorAll(".sx-pa-parallax"));
		if (parallax.length) {
			var ticking = false;
			var moveAll = function () {
				ticking = false;
				parallax.forEach(function (hero) {
					var r = hero.getBoundingClientRect();
					if (r.bottom < 0 || r.top > window.innerHeight) return;
					hero.style.setProperty("--sx-par", Math.round(-r.top * 0.12) + "px");
				});
			};
			window.addEventListener("scroll", function () {
				if (!ticking) { ticking = true; requestAnimationFrame(moveAll); }
			}, { passive: true });
			moveAll();
		}

		// --- a pause button on every hero that keeps moving -------------------
		// WCAG 2.2.2: anything that moves by itself for more than 5 seconds
		// (sliding photos, the photo strip, the turning badge, drifting
		// lights, slow zoom, a rotating word, a video) can be paused.
		function keepsMoving(hero) {
			if (hero.querySelector("video[autoplay]")) return true;
			if (!reduceMotion && hero.querySelector("[data-sx-rotate] em")) return true;
			var auto = Array.prototype.some.call(hero.querySelectorAll("[data-sx-slides]"), function (b) { return b._sxAuto; });
			if (auto) return true;
			if (!hero.getAnimations) return false;
			return hero.getAnimations({ subtree: true }).some(function (a) {
				return a.effect && a.effect.getComputedTiming().iterations === Infinity;
			});
		}
		each(app.querySelectorAll("[data-sx-hero]"), function (hero) {
			var videos = hero.querySelectorAll("video");
			if (reduceMotion) each(videos, function (v) { v.removeAttribute("autoplay"); v.pause(); });
			if (!keepsMoving(hero)) return;
			var b = el("button", "sx-pause");
			b.type = "button";
			b.setAttribute("aria-pressed", "false");
			b.setAttribute("aria-label", "Pause motion");
			b.addEventListener("click", function () {
				var stop = !hero.classList.contains("is-paused");
				hero.classList.toggle("is-paused", stop);
				b.setAttribute("aria-pressed", stop ? "true" : "false");
				b.setAttribute("aria-label", stop ? "Play motion" : "Pause motion");
				each(videos, function (v) { if (stop) v.pause(); else v.play().catch(function () {}); });
			});
			hero.appendChild(b);
		});

		// --- before / after slider ------------------------------------------
		each(app.querySelectorAll("[data-sx-ba]"), function (ba) {
			var range = ba.querySelector(".sx-ba-range");
			range.addEventListener("input", function () { ba.style.setProperty("--pos", range.value + "%"); });
		});

		// --- rotating big quote (several reviews, lively motion) -------------
		each(app.querySelectorAll(".sx-reviews--big_quote"), function (sec) {
			var quotes = sec.querySelectorAll("[data-sx-quote]");
			if (quotes.length < 2 || motion === "none") return;
			var i = 0;
			setInterval(function () {
				quotes[i].hidden = true;
				i = (i + 1) % quotes.length;
				quotes[i].hidden = false;
			}, 6000);
		});

		// --- progress --------------------------------------------------------
		function stage() {
			if (state.slot) return 5;
			if (state.chosen.length && els.date && els.date.value) return 4;
			if (state.chosen.length) return 3;
			if (els.branch && els.branch.value) return 2;
			return 1;
		}
		function updateSteps() {
			if (!els.steps || wizard) return;
			var s = stage();
			each(els.steps.children, function (n, i) { n.classList.toggle("active", i < s); });
		}

		// --- wizard (Step By Step) ------------------------------------------
		function canLeave(step) {
			if (step === 1) return !!els.branch.value && staffChosen();
			if (step === 2) return state.chosen.length > 0;
			if (step === 3) return !!state.slot;
			return true;
		}
		function showStep(n) {
			if (!wizard) return;
			state.step = n;
			each(form.querySelectorAll(".sx-step"), function (s) {
				s.classList.toggle("is-current", +s.getAttribute("data-step") === n);
			});
			each(form.querySelectorAll(".sx-step-dot"), function (d) {
				var k = +d.getAttribute("data-dot");
				d.classList.toggle("done", k < n);
				d.classList.toggle("current", k === n);
			});
			var back = form.querySelector("[data-wiz=back]");
			var next = form.querySelector("[data-wiz=next]");
			if (back) back.style.visibility = n > 1 ? "visible" : "hidden";
			if (next) {
				next.hidden = n === 4;
				next.disabled = !canLeave(n);
			}
		}
		function refreshWizard() { if (wizard) showStep(state.step); }
		if (wizard) {
			form.addEventListener("click", function (e) {
				var b = e.target.closest("[data-wiz]");
				if (!b) return;
				if (b.getAttribute("data-wiz") === "back") showStep(Math.max(1, state.step - 1));
				else if (canLeave(state.step)) showStep(Math.min(4, state.step + 1));
			});
			showStep(1);
		}

		// --- basket ----------------------------------------------------------
		function totals() {
			var mins = 0, sum = 0;
			state.chosen.forEach(function (name) {
				var s = state.catalogue.filter(function (c) { return c.service === name; })[0];
				if (s) { mins += s.duration_minutes || 0; sum += Number(s.price) || 0; }
			});
			return { minutes: mins, price: sum };
		}
		function renderBasket() {
			if (!els.basket) return;
			var t = totals();
			els.basket.textContent = state.chosen.length
				? state.chosen.length + " service" + (state.chosen.length > 1 ? "s" : "") +
				  " · " + t.minutes + " min · " + money(t.price)
				: "";
		}

		function resetBelow(level) {
			if (!hasForm) return;
			if (level <= 1) {
				state.chosen = [];
				els.services.innerHTML = "";
				els.staff.innerHTML = STAFF_PROMPT;
				els.staff.disabled = true;
				renderBasket();
			}
			els.date.value = "";
			els.date.disabled = true;
			renderDateStrip();
			els.slots.innerHTML = "<span class='sx-hint'>Pick your services and a date to see times.</span>";
			els.customer.hidden = true;
			els.selected.hidden = true;
			els.confirm.hidden = true;
			els.result.hidden = true;
			state.slot = null;
			updateSteps();
			refreshWizard();
		}

		// --- branches --------------------------------------------------------
		call("get_branches").then(function (branches) {
			state.branches = branches || [];
			if (hasForm) {
				els.branch.innerHTML = "<option value=''>Select a salon…</option>";
				state.branches.forEach(function (b) {
					var o = el("option", null, b.branch_name + (b.city ? " — " + b.city : ""));
					o.value = b.name;
					els.branch.appendChild(o);
				});
			}
			// The page's own branch (a branch page, or the first branch on the
			// home page) — the one the server already drew services and team for.
			var start = app.getAttribute("data-start-branch");
			if (!branchByName(start)) start = state.branches.length ? state.branches[0].name : "";
			if (start) setBranch(start);
		}).catch(function (err) {
			// Only the form needs the list; a page without one stays quiet.
			if (hasForm) showProblem(err, "We couldn't load the booking form");
		});
		function branchByName(name) {
			return state.branches.filter(function (b) { return b.name === name; })[0];
		}
		function markBranch(name) {
			each(app.querySelectorAll(".sx-branch"), function (c) {
				c.classList.toggle("active", c.dataset.branch === name);
			});
		}
		function setBranch(name) {
			markBranch(name);
			renderShowcase(name);
			if (hasForm && els.branch.value !== name) {
				els.branch.value = name;
				onBranchChange();
			}
		}

		// --- Cloudflare's human check --------------------------------------------
		// Off unless the page carries a site key. Cloudflare's rules: load
		// api.js from its exact address, never proxied; a token is single-use
		// and lives 5 minutes, so every request takes a fresh one and the
		// widget is reset afterwards; the SERVER verifies it (human_check.py).
		var humanKey = app.getAttribute("data-human-key") || "";
		var humans = {};   // container id -> { id, token, waiting: [] }
		var turnstileLoading = null;
		function loadTurnstile() {
			if (window.turnstile) return Promise.resolve(window.turnstile);
			if (turnstileLoading) return turnstileLoading;
			turnstileLoading = new Promise(function (resolve, reject) {
				window.__sxTurnstileReady = function () { resolve(window.turnstile); };
				var sc = document.createElement("script");
				sc.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__sxTurnstileReady";
				sc.async = true;
				sc.onerror = function () { turnstileLoading = null; reject(new Error("load")); };
				document.head.appendChild(sc);
			});
			return turnstileLoading;
		}
		function humanWidget(containerId, action) {
			if (!humanKey || !$(containerId)) return Promise.resolve(null);
			if (humans[containerId]) return Promise.resolve(humans[containerId]);
			return loadTurnstile().then(function (ts) {
				if (humans[containerId]) return humans[containerId];
				var h = { id: null, token: "", waiting: [] };
				humans[containerId] = h;
				h.id = ts.render("#" + containerId, {
					sitekey: humanKey,
					action: action,
					appearance: "interaction-only",
					size: "flexible",
					theme: app.getAttribute("data-human-theme") || "auto",
					callback: function (token) {
						h.token = token;
						h.waiting.splice(0).forEach(function (w) { w.resolve(token); });
					},
					"expired-callback": function () { h.token = ""; },
					"error-callback": function () {
						h.token = "";
						h.waiting.splice(0).forEach(function (w) { w.reject(new Error("check")); });
						return true;   // handled here (no console warning); Cloudflare retries itself
					}
				});
				return h;
			});
		}
		// A token for one request: the current one, or wait for Cloudflare.
		function humanToken(containerId, action) {
			if (!humanKey || !$(containerId)) return Promise.resolve(null);
			return humanWidget(containerId, action).then(function (h) {
				if (!h) return null;
				if (h.token) { var t = h.token; h.token = ""; return t; }
				return new Promise(function (resolve, reject) {
					var w = { resolve: function (t) { h.token = ""; resolve(t); }, reject: reject };
					h.waiting.push(w);
					setTimeout(function () {
						var i = h.waiting.indexOf(w);
						if (i >= 0) { h.waiting.splice(i, 1); reject(new Error("slow")); }
					}, 20000);
				});
			});
		}
		// Tokens are single-use: after any request, ask Cloudflare for a new one.
		function humanUsed(containerId) {
			var h = humans[containerId];
			if (h && window.turnstile) { h.token = ""; window.turnstile.reset(h.id); }
		}
		// Cloudflare not answering in time becomes an ordinary message.
		function humanSlow() { throw problem(MSG_HUMAN, "One moment"); }

		// --- showcase sections: services and team ----------------------------
		function bookWith(staff) {
			if (hasForm) { els.staff.value = staff; loadServices(); }
			openBooking();
		}
		function showTab(tab) {
			var box = tab.closest("[data-sx-services]");
			var gi = tab.getAttribute("data-sx-tab");
			each(box.querySelectorAll("[data-sx-tab]"), function (t) { t.classList.toggle("on", t === tab); });
			each(box.querySelectorAll("[data-group]"), function (r) { r.hidden = r.getAttribute("data-group") !== gi; });
		}
		function bookService(service) {
			if (hasForm && !staffChosen()) {
				// Services wait for a stylist: remember this one, tick it
				// once they choose, and point them at the stylist box.
				state.pendingService = service;
				openBooking();
				if (wizard) showStep(1);
				if (!els.staff.disabled) setTimeout(function () { els.staff.focus(); }, 350);
				toast(anyAllowed() ? "Choose a stylist, or First available." : "Choose a stylist.");
				return;
			}
			if (hasForm) {
				var cb = els.services.querySelector("input[value='" + CSS.escape(service) + "']");
				if (cb && !cb.checked) { cb.checked = true; cb.dispatchEvent(new Event("change")); }
				if (wizard && state.step < 3 && canLeave(1)) showStep(3);
			}
			openBooking();
		}

		// Boxes already showing this branch (drawn by the server) are left as
		// they are; only a switch to another branch redraws them.
		function stale(selector, branchName) {
			return Array.prototype.filter.call(app.querySelectorAll(selector), function (box) {
				return box.getAttribute("data-for-branch") !== branchName;
			});
		}
		function renderShowcase(branchName) {
			var serviceBoxes = stale("[data-sx-services]", branchName);
			if (serviceBoxes.length) {
				call("get_services", { branch: branchName }).catch(function () { return []; })
					.then(function (services) {
						services = services || [];
						if (!state.currency && services.length) state.currency = services[0].currency || "";
						serviceBoxes.forEach(function (box) {
							renderServices(box, services);
							box.setAttribute("data-for-branch", branchName);
						});
					});
			}
			var teamBoxes = stale("[data-sx-team]", branchName);
			if (teamBoxes.length) {
				call("get_staff", { branch: branchName }).catch(function () { return []; })
					.then(function (staff) {
						teamBoxes.forEach(function (box) {
							renderTeam(box, staff || []);
							box.setAttribute("data-for-branch", branchName);
						});
					});
			}
		}

		function renderServices(box, services) {
			var design = box.getAttribute("data-design");
			box.innerHTML = "";
			if (!services.length) {
				box.appendChild(el("div", "sx-hint sx-center", "Services will appear here soon."));
				return;
			}
			if (design === "hero_card") {
				box.appendChild(el("div", "sx-eyebrow", "Services & prices"));
				services.slice(0, 6).forEach(function (s) {
					var row = el("div", "sx-mc-row");
					row.setAttribute("data-service", s.service);
					var left = el("span");
					left.appendChild(el("b", null, s.service_name));
					left.appendChild(el("small", null, s.duration_minutes + " min"));
					row.appendChild(left);
					row.appendChild(el("span", "sx-price", money(s.price)));
					box.appendChild(row);
				});
				if (services.length > 6) {
					var more = el("a", "sx-mc-more", "+" + (services.length - 6) + " more");
					more.href = "#booking";
					more.setAttribute("data-book", "");
					box.appendChild(more);
				}
			} else if (design === "price_menu") {
				groupBy(services, "item_group").forEach(function (g) {
					var group = el("div", "sx-menu-group");
					group.appendChild(el("h3", null, g.name));
					var items = el("div", "sx-menu-items");
					g.items.forEach(function (s) {
						var it = el("div", "sx-menu-item");
						it.setAttribute("data-service", s.service);
						var line = el("div", "sx-menu-line");
						line.appendChild(el("b", null, s.service_name));
						line.appendChild(el("i"));
						line.appendChild(el("span", null, money(s.price)));
						it.appendChild(line);
						it.appendChild(el("small", null, s.duration_minutes + " min"));
						items.appendChild(it);
					});
					group.appendChild(items);
					box.appendChild(group);
				});
			} else if (design === "soft_list") {
				var groups = groupBy(services, "item_group");
				var list = el("div", "sx-soft-list");
				if (groups.length > 1) {
					var tabs = el("div", "sx-tabs");
					groups.forEach(function (g, i) {
						var t = el("button", "sx-tab" + (i ? "" : " on"), g.name);
						t.type = "button";
						t.setAttribute("data-sx-tab", String(i));
						tabs.appendChild(t);
					});
					box.appendChild(tabs);
				}
				groups.forEach(function (g, gi) {
					g.items.forEach(function (s) {
						var row = el("div", "sx-soft-row");
						row.setAttribute("data-service", s.service);
						row.setAttribute("data-group", String(gi));
						row.hidden = gi > 0;
						var left = el("div");
						left.appendChild(el("b", null, s.service_name));
						left.appendChild(el("small", null, s.duration_minutes + " min"));
						row.appendChild(left);
						row.appendChild(el("span", "sx-price", money(s.price)));
						list.appendChild(row);
					});
				});
				box.appendChild(list);
			} else if (design === "tiles") {
				var tiles = el("div", "sx-tiles");
				services.forEach(function (s) {
					var t = el("div", "sx-tile");
					t.setAttribute("data-service", s.service);
					t.appendChild(img(s.image, s.service_name));
					var copy = el("div", "sx-tile-copy");
					var left = el("div");
					left.appendChild(el("b", null, s.service_name));
					left.appendChild(el("small", null, s.duration_minutes + " min"));
					copy.appendChild(left);
					copy.appendChild(el("span", "sx-chip", money(s.price)));
					t.appendChild(copy);
					tiles.appendChild(t);
				});
				box.appendChild(tiles);
			} else { // cards
				var grid = el("div", "sx-card-grid");
				services.forEach(function (s) {
					var c = el("article", "sx-card");
					var media = el("div", "sx-card-media");
					media.appendChild(img(s.image, s.service_name));
					c.appendChild(media);
					var body = el("div", "sx-card-body");
					var top = el("div", "sx-card-top");
					top.appendChild(el("h3", "sx-h3", s.service_name));
					top.appendChild(el("span", "sx-chip", money(s.price)));
					body.appendChild(top);
					var foot = el("div", "sx-card-foot");
					foot.appendChild(el("span", null, s.duration_minutes + " min"));
					var b = el("button", "sx-btn sx-btn-sm", "Book");
					b.type = "button";
					b.setAttribute("data-service", s.service);
					foot.appendChild(b);
					body.appendChild(foot);
					c.appendChild(body);
					grid.appendChild(c);
				});
				box.appendChild(grid);
			}
		}

		function renderTeam(box, staff) {
			var design = box.getAttribute("data-design");
			box.className = "sx-team-list sx-reveal is-in sx-team--" + design;
			box.innerHTML = "";
			if (!staff.length) {
				box.appendChild(el("div", "sx-hint sx-center", "Our team will appear here soon."));
				return;
			}
			var grid = el("div", "sx-team-grid");
			staff.forEach(function (m) {
				var p = el("div", "sx-person");
				var media = el("div", "sx-person-media");
				media.appendChild(img(m.photo, m.staff_name));
				p.appendChild(media);
				p.appendChild(el("h3", null, m.staff_name));
				p.appendChild(el("div", "sx-eyebrow", m.designation || "Stylist"));
				if (design === "cards") {
					if (m.bio) p.appendChild(el("p", null, m.bio));
					var b = el("button", "sx-btn sx-btn-ghost sx-btn-sm", "Book with " + m.staff_name);
					b.type = "button";
					b.setAttribute("data-staff", m.name);
					p.appendChild(b);
				}
				grid.appendChild(p);
			});
			box.appendChild(grid);
		}

		if (!hasForm) {
			bindManage();
			return;
		}

		// --- the booking flow --------------------------------------------------
		els.branch.addEventListener("change", onBranchChange);

		function onBranchChange() {
			resetBelow(1);
			markBranch(els.branch.value);
			if (!els.branch.value) return;
			// "Today" is the BRANCH's day, sent by the server — a customer in
			// another timezone still sees the branch's real calendar.
			var b = branchByName(els.branch.value);
			if (b && b.today) els.date.min = b.today;
			renderShowcase(els.branch.value);
			// Stylist first: the service list depends on who does the work.
			Promise.all([
				call("get_staff", { branch: els.branch.value }),
				call("get_services", { branch: els.branch.value }).catch(function () { return []; })
			]).then(function (res) {
				var staff = res[0] || [];
				state.branchCatalogue = res[1] || [];
				els.staff.innerHTML = STAFF_PROMPT;
				if (anyAllowed()) {
					var any = el("option", null, "First available");
					any.value = ANY_STAFF;
					els.staff.appendChild(any);
				}
				staff.forEach(function (m) {
					var o = el("option", null, m.staff_name + (m.designation ? " (" + m.designation + ")" : ""));
					o.value = m.name;
					els.staff.appendChild(o);
				});
				els.staff.disabled = false;
				loadServices();
			}).catch(function (err) {
				showProblem(err, "We couldn't load the stylists");
			});
			updateSteps();
			refreshWizard();
		}

		function loadServices() {
			state.chosen = [];
			els.services.innerHTML = "";
			renderBasket();
			onBasketChange();
			if (!staffChosen()) {
				els.services.appendChild(el("span", "sx-hint", anyAllowed()
					? "Choose a stylist, or First available, to see the services."
					: "Choose a stylist to see the services."));
				refreshWizard();
				return;
			}
			call("get_services", { branch: els.branch.value, staff: staffArg() })
				.then(function (services) {
					services = services || [];
					state.catalogue = services;
					state.currency = (services[0] && services[0].currency) || state.currency;
					var groups = groupBy(services, "item_group");
					groups.forEach(function (g) {
						if (groups.length > 1) els.services.appendChild(el("div", "sx-group-label", g.name));
						g.items.forEach(function (s) {
							var row = el("label", "sx-pick");
							var cb = el("input");
							cb.type = "checkbox";
							cb.value = s.service;
							cb.addEventListener("change", function () {
								if (cb.checked) state.chosen.push(s.service);
								else state.chosen = state.chosen.filter(function (x) { return x !== s.service; });
								row.classList.toggle("checked", cb.checked);
								renderBasket();
								onBasketChange();
							});
							row.appendChild(cb);
							row.appendChild(el("b", null, s.service_name));
							row.appendChild(el("span", null, s.duration_minutes + " min · " + money(s.price)));
							els.services.appendChild(row);
						});
					});
					if (!services.length) showEmptyServices();
					// A service picked on the page before a stylist was chosen.
					var wanted = state.pendingService;
					state.pendingService = null;
					if (wanted) {
						var cb = els.services.querySelector("input[value='" + CSS.escape(wanted) + "']");
						if (cb && !cb.checked) { cb.checked = true; cb.dispatchEvent(new Event("change")); }
					}
					refreshWizard();
				})
				.catch(function (err) {
					els.services.innerHTML = "";
					els.services.appendChild(el("span", "sx-hint", asProblem(err).message));
				});
		}

		// Two quite different causes of "nothing bookable" — say which.
		function showEmptyServices() {
			var msg;
			if (!state.branchCatalogue.length) msg = "This salon has no services set up yet.";
			else if (staffArg()) {
				msg = els.staff.options[els.staff.selectedIndex].text +
					" does not do any of the services here. " +
					(anyAllowed() ? "Try another stylist, or First available." : "Try another stylist.");
			} else msg = "Nothing bookable here yet.";
			els.services.innerHTML = "";
			els.services.appendChild(el("span", "sx-hint", msg));
		}

		function onBasketChange() {
			els.date.disabled = !state.chosen.length;
			if (!state.chosen.length) {
				els.date.value = "";
				els.slots.innerHTML = "<span class='sx-hint'>Pick your services and a date to see times.</span>";
			}
			els.customer.hidden = true;
			els.selected.hidden = true;
			els.confirm.hidden = true;
			state.slot = null;
			renderDateStrip();
			if (!els.date.value && state.chosen.length) {
				// Start on the branch's today so times show without a click.
				var b = branchByName(els.branch.value);
				if (b && b.today) { els.date.value = b.today; markStripDate(b.today); }
			}
			loadSlots();
			updateSteps();
			refreshWizard();
		}

		els.staff.addEventListener("change", function () {
			els.result.hidden = true;   // a new booking begins
			loadServices();
		});
		// After a booking: back to the start, as if the page were fresh — the
		// salon stays chosen, everything else (stylist, services, date, time,
		// name, phone, email, notes, the texts box) is emptied.
		function startOver() {
			state.pendingService = null;
			els.staff.value = "";
			loadServices();
			[els.name, els.phone, els.email, els.notes].forEach(function (input) {
				if (input) { input.value = ""; fieldError(input, ""); }
			});
			if ($("sb-sms-ok")) $("sb-sms-ok").checked = false;
			els.confirm.disabled = false;
			if (wizard) showStep(1);
			updateSteps();
		}
		els.date.addEventListener("change", function () {
			markStripDate(els.date.value);
			loadSlots();
		});

		// Seven quick-pick days from the branch's own today.
		function renderDateStrip() {
			if (!els.dateStrip) return;
			els.dateStrip.innerHTML = "";
			var b = branchByName(els.branch.value);
			var start = b && b.today ? parseDay(b.today) : new Date();
			for (var i = 0; i < 7; i++) {
				(function (i) {
					var d = new Date(start);
					d.setDate(start.getDate() + i);
					var val = dayString(d);
					var btn = el("button", "sx-date-btn");
					btn.type = "button";
					btn.dataset.date = val;
					btn.disabled = !state.chosen.length;
					btn.appendChild(el("small", null, d.toLocaleDateString(undefined, { weekday: "short" })));
					btn.appendChild(document.createTextNode(d.toLocaleDateString(undefined, { month: "short", day: "numeric" })));
					btn.addEventListener("click", function () {
						els.date.value = val;
						markStripDate(val);
						loadSlots();
					});
					els.dateStrip.appendChild(btn);
				})(i);
			}
			markStripDate(els.date.value);
		}
		function markStripDate(val) {
			if (!els.dateStrip) return;
			each(els.dateStrip.children, function (c) { c.classList.toggle("active", !!val && c.dataset.date === val); });
		}

		function loadSlots() {
			state.slot = null;
			els.customer.hidden = true;
			els.selected.hidden = true;
			els.confirm.hidden = true;
			updateSteps();
			refreshWizard();
			if (!state.chosen.length || !els.date.value) return;
			els.slots.innerHTML = "<span class='sx-hint'>Loading…</span>";
			call("get_available_slots", {
				branch: els.branch.value,
				date: els.date.value,
				services: state.chosen,
				staff: staffArg()
			})
				.then(renderSlots)
				.catch(function (err) {
					// In the times box, in the server's own words ("Bookings are
					// only open up to 30 days in advance.") — no dialog.
					els.slots.innerHTML = "";
					els.slots.appendChild(el("span", "sx-hint", asProblem(err).message));
				});
		}

		function renderSlots(slots) {
			els.slots.innerHTML = "";
			if (!slots || !slots.length) {
				els.slots.innerHTML = "<span class='sx-hint'>No times left that day for that combination.</span>";
				return;
			}
			slots.forEach(function (slot) {
				var btn = el("button", "sx-slot", slot.label);
				btn.type = "button";
				btn.addEventListener("click", function () {
					each(els.slots.querySelectorAll(".sx-slot"), function (b) { b.classList.remove("active"); });
					btn.classList.add("active");
					state.slot = slot;
					els.customer.hidden = false;
					els.confirm.hidden = false;
					els.confirm.disabled = false;
					humanWidget("sb-human", "book");
					var t = totals();
					els.selected.hidden = false;
					els.selected.textContent =
						slot.label + (staffArg() ? " with " + slot.staff_name : "") +
						" · " + t.minutes + " min · " + money(t.price);
					updateSteps();
					// In the wizard a chosen time is the end of step 3.
					if (wizard) showStep(4); else refreshWizard();
				});
				els.slots.appendChild(btn);
			});
		}

		clearOnType(els.name);
		clearOnType(els.phone);
		// The server refuses a time that has gone with one of these titles
		// (booking._assign_staff / _validate_no_overlap: "Slot Not Available";
		// time off: "Not available") — the times are then out of date.
		var GONE_TITLES = ["Slot Not Available", "Not available"];
		els.confirm.addEventListener("click", function () {
			if (!state.slot) return;
			var missing = [];
			if (!els.name.value.trim()) { fieldError(els.name, "Please enter your name."); missing.push(els.name); }
			else fieldError(els.name, "");
			if (!els.phone.value.trim()) { fieldError(els.phone, "Please enter your phone number."); missing.push(els.phone); }
			else fieldError(els.phone, "");
			if (missing.length) { showField(missing[0]); return; }
			els.confirm.disabled = true;
			var texted = !!($("sb-sms-ok") && $("sb-sms-ok").checked);
			humanToken("sb-human", "book")
				.catch(humanSlow)
				.then(function (token) {
					return call("create_appointment", {
						branch: els.branch.value,
						// Null means "first available": the server picks at
						// confirmation, among those still free right then.
						staff: staffArg(),
						date: els.date.value,
						time: state.slot.time,
						services: state.chosen,
						customer_name: els.name.value,
						customer_phone: els.phone.value,
						customer_email: els.email.value || null,
						notes: els.notes.value || null,
						// The appointment-texts box above Confirm (unticked to
						// start with): ticked = this booking's confirmation and
						// reminder texts.
						sms_consent: $("sb-sms-ok") && $("sb-sms-ok").checked ? 1 : 0,
						human_token: token
					});
				})
				.then(function (res) {
					// Read before the form starts over.
					var when = (res.date_label || res.appointment_date) + " at " + state.slot.label +
						(res.staff_name ? " with " + res.staff_name : "");
					var number = "Confirmation #" + (res.confirmation_number || "");
					// "Also in your text" only when texts were agreed to — an
					// unticked box sends none (salon/sms.py's consent gate).
					var keep = "Keep this number" + (texted ? " (it is also in your text)" : "") +
						". To cancel, use My bookings with your phone number and this number.";
					// The number stays on the page after the dialog closes,
					// until the visitor starts another booking.
					els.result.hidden = false;
					els.result.innerHTML = "";
					var ok = el("div", "sx-success");
					ok.appendChild(el("strong", null, res.message || "Booked."));
					ok.appendChild(el("div", "sx-confirmation-number", number));
					ok.appendChild(el("div", null, when));
					ok.appendChild(el("div", "sx-hint", keep));
					els.result.appendChild(ok);
					// A finished booking can never be confirmed twice: the form
					// starts over and Cloudflare's single-use token is renewed.
					humanUsed("sb-human");
					startOver();
					ask({
						title: "Booking confirmed",
						message: res.message || "",
						lines: [["sx-msg-number", res.confirmation_number || ""], ["sx-msg-when", when], ["sx-msg-note", keep]],
						ok: "Done",
						success: true
					});
				})
				.catch(function (err) {
					err = asProblem(err);
					humanUsed("sb-human");
					els.confirm.disabled = false;
					// Taken meanwhile: fresh times are already showing when the
					// visitor closes the message. What they typed stays.
					if (GONE_TITLES.indexOf(err.sxTitle) >= 0) {
						loadSlots();
						if (wizard) showStep(3);
					}
					showProblem(err, "We couldn't book that");
				});
		});

		bindManage();

		// --- my bookings: find and cancel by phone ---------------------------
		function bindManage() {
			if (!els.manageBtn) return;
			// Start Cloudflare's check as soon as someone heads for the box,
			// so a token is usually ready by the time they tap Find.
			[els.managePhone, els.manageNumber].forEach(function (input) {
				if (input) input.addEventListener("focus", function () { humanWidget("sb-manage-human", "manage"); });
			});
			// The two boxes sit side by side, so their messages go in the line
			// under the row, not inside it.
			var errorsUnder = $("sb-manage-errors");
			clearOnType(els.managePhone, errorsUnder);
			clearOnType(els.manageNumber, errorsUnder);
			els.manageBtn.addEventListener("click", function () {
				var phone = els.managePhone.value;
				var number = els.manageNumber ? els.manageNumber.value : "";
				var missing = [];
				if (!phone.trim()) { fieldError(els.managePhone, "Please enter your phone number.", errorsUnder); missing.push(els.managePhone); }
				else fieldError(els.managePhone, "", errorsUnder);
				if (els.manageNumber && !number.trim()) {
					fieldError(els.manageNumber, "Please enter the confirmation number from your text.", errorsUnder);
					missing.push(els.manageNumber);
				} else fieldError(els.manageNumber, "", errorsUnder);
				if (missing.length) { showField(missing[0]); return; }
				els.manageList.innerHTML = "<span class='sx-hint'>Looking…</span>";
				humanToken("sb-manage-human", "manage")
					.catch(humanSlow)
					.then(function (token) {
						return call("find_booking", { phone: phone, confirmation_number: number, human_token: token });
					})
					.then(function (b) { renderManage(b, phone, number); })
					.catch(function (err) {
						// Under the boxes, in the server's words: "Booking not
						// found…", or "Too many requests…" — never a false
						// "not found" for a different problem.
						els.manageList.innerHTML = "";
						var note = el("div", "sx-notice", asProblem(err).message);
						note.setAttribute("role", "alert");
						els.manageList.appendChild(note);
					})
					.then(function () { humanUsed("sb-manage-human"); });
			});
		}
		function renderManage(b, phone, number) {
			els.manageList.innerHTML = "";
			if (!b) return;
			var card = el("div", "sx-card sx-booking-card");
			card.appendChild(el("div", "sx-booking-when", (b.date_label || b.appointment_date) + " · " + b.from_time_label));
			card.appendChild(el("div", "sx-booking-what",
				(b.services || []).join(", ") + (b.staff_name ? " — " + b.staff_name : "")));
			card.appendChild(el("div", "sx-hint",
				(state.branches.length > 1 && b.branch ? b.branch + " · " : "") + "Confirmation #" + b.confirmation_number));
			if (b.can_cancel) {
				var cancel = el("button", "sx-btn sx-btn-ghost sx-btn-sm", "Cancel booking");
				cancel.type = "button";
				cancel.addEventListener("click", function () {
					// One tap used to cancel at once; now the visitor confirms.
					ask({
						title: "Cancel this booking?",
						message: (b.date_label || b.appointment_date) + " at " + b.from_time_label +
							(b.staff_name ? " with " + b.staff_name : "") + ". This can't be undone online.",
						ok: "Yes, cancel it",
						cancel: "Keep it"
					}).then(function (yes) {
						if (!yes) return;
						cancel.disabled = true;
						humanToken("sb-manage-human", "manage")
							.catch(humanSlow)
							.then(function (token) {
								return call("cancel_booking", { phone: phone, confirmation_number: number, human_token: token });
							})
							.then(function () {
								card.classList.add("cancelled");
								cancel.textContent = "Cancelled";
								toast("Booking cancelled");
							})
							.catch(function (err) {
								cancel.disabled = false;
								showProblem(err, "We couldn't cancel that");
							})
							.then(function () { humanUsed("sb-manage-human"); });
					});
				});
				card.appendChild(cancel);
			} else {
				card.appendChild(el("div", "sx-hint", b.status === "Cancelled" ? "This booking was cancelled." :
					"This booking can no longer be changed online. Please call the salon."));
			}
			els.manageList.appendChild(card);
		}
	}

	if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
	else init();
})();
